import { FC } from 'react';

import { amountLabel, formatSettlementAmount } from '../../utils/coupon-settlement.utils';

/** 집행 확인용 제외 행 — 운영자가 체크 해제한 쿠폰 (발송/환불 라인 단위 표기) */
export interface ExcludedPendingRow {
  key: string;
  couponId: string;
  kind: 'send' | 'refund';
  code: string;
  amount: number;
}

export interface CouponSettleConfirmModalProps {
  brandName: string;
  /** 기간 표기 — 조회 확정(appliedRange) 기준 문자열 (호출부에서 조립) */
  rangeLabel: string;
  /** 축 토글 (수익금만/환불만 집행) — 끈 축은 미정산에 남아 다음 집행에서 잡힌다 */
  axisSend: boolean;
  axisRefund: boolean;
  onToggleAxisSend: () => void;
  onToggleAxisRefund: () => void;
  /** 포함분(초과·체크 해제 제외 후) — 축 토글과 무관한 원본 건수·금액 */
  includedSendCount: number;
  includedSendSum: number;
  includedRefundCount: number;
  includedRefundSum: number;
  /** 축 토글 반영 최종값 — 서버 대조값(expectedTotalAmount)·집행 건수 */
  effExpected: number;
  effCount: number;
  /** 구매수량 초과 자동 제외 건수 — 현황 안내 전용 */
  overQuotaCount: number;
  excludedRows: ExcludedPendingRow[];
  /** 제외 복원 (쿠폰 단위 토글) */
  onRestore: (couponId: string) => void;
  memo: string;
  onMemo: (value: string) => void;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * 쿠폰 정산 집행 확인 모달 — 집행 조작(축 토글·제외 복원)의 최종 관문.
 * 미정산 상세 모달(z-40) 위에 뜬다(z-50). 열고 닫기는 호출부 state가 관리.
 */
export const CouponSettleConfirmModal: FC<CouponSettleConfirmModalProps> = ({
  brandName,
  rangeLabel,
  axisSend,
  axisRefund,
  onToggleAxisSend,
  onToggleAxisRefund,
  includedSendCount,
  includedSendSum,
  includedRefundCount,
  includedRefundSum,
  effExpected,
  effCount,
  overQuotaCount,
  excludedRows,
  onRestore,
  memo,
  onMemo,
  busy,
  onConfirm,
  onClose,
}) => {
  /** 한쪽 축을 끈 집행 — by-filter 축 플래그로 나간다 (안내 문구 분기) */
  const axisSplit = !axisSend || !axisRefund;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-base font-bold text-gray-800">쿠폰 정산 집행</h3>

        {/* 대상·기간 (기간 변경은 재조회가 필요해 페이지 필터에서만) */}
        <div className="mb-3 rounded-lg bg-gray-50 px-4 py-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">대상 상조</span>
            <span className="font-semibold text-gray-800">{brandName}</span>
          </div>
          <div className="mt-1.5 flex items-center justify-between">
            <span className="text-gray-500">기간(발송/환불완료일)</span>
            <span className="font-medium text-gray-700">{rangeLabel}</span>
          </div>
        </div>

        {/* 발송/환불 카드 = 축 토글 (한쪽만 집행 조작) · 제외 카드 = 현황 */}
        <div className="mb-3 grid grid-cols-3 gap-2 text-center">
          <button
            type="button"
            onClick={onToggleAxisSend}
            className={`rounded-lg border px-2 py-3 transition-colors ${
              axisSend
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-gray-200 bg-gray-100 opacity-70'
            }`}
          >
            <p
              className={`flex items-center justify-center gap-1 text-xs ${
                axisSend ? 'text-emerald-600' : 'text-gray-500'
              }`}
            >
              <input
                type="checkbox"
                checked={axisSend}
                readOnly
                className="pointer-events-none h-3 w-3 rounded border-gray-300"
              />
              발송 수익금
            </p>
            <p
              className={`mt-1 text-lg font-bold tabular-nums ${
                axisSend ? 'text-emerald-700' : 'text-gray-400 line-through'
              }`}
            >
              {includedSendCount}건
            </p>
            <p
              className={`text-xs font-medium tabular-nums ${
                axisSend ? 'text-emerald-700' : 'text-gray-400'
              }`}
            >
              {axisSend
                ? `+${includedSendSum.toLocaleString('ko-KR')}`
                : '이번 집행 제외'}
            </p>
          </button>
          <button
            type="button"
            onClick={onToggleAxisRefund}
            className={`rounded-lg border px-2 py-3 transition-colors ${
              axisRefund
                ? 'border-rose-300 bg-rose-50'
                : 'border-gray-200 bg-gray-100 opacity-70'
            }`}
          >
            <p
              className={`flex items-center justify-center gap-1 text-xs ${
                axisRefund ? 'text-rose-600' : 'text-gray-500'
              }`}
            >
              <input
                type="checkbox"
                checked={axisRefund}
                readOnly
                className="pointer-events-none h-3 w-3 rounded border-gray-300"
              />
              환불 차감
            </p>
            <p
              className={`mt-1 text-lg font-bold tabular-nums ${
                axisRefund ? 'text-rose-700' : 'text-gray-400 line-through'
              }`}
            >
              {includedRefundCount}건
            </p>
            <p
              className={`text-xs font-medium tabular-nums ${
                axisRefund ? 'text-rose-700' : 'text-gray-400'
              }`}
            >
              {axisRefund
                ? `−${includedRefundSum.toLocaleString('ko-KR')}`
                : '이번 집행 제외'}
            </p>
          </button>
          <div
            className={`rounded-lg border px-2 py-3 ${
              excludedRows.length > 0
                ? 'border-amber-300 bg-amber-50'
                : 'border-gray-200 bg-gray-50'
            }`}
          >
            <p
              className={`text-xs ${
                excludedRows.length > 0 ? 'text-amber-600' : 'text-gray-400'
              }`}
            >
              체크 해제 제외
            </p>
            <p
              className={`mt-1 text-lg font-bold tabular-nums ${
                excludedRows.length > 0 ? 'text-amber-700' : 'text-gray-400'
              }`}
            >
              {excludedRows.length}건
            </p>
            <p
              className={`text-xs ${
                excludedRows.length > 0 ? 'text-amber-600' : 'text-gray-300'
              }`}
            >
              {excludedRows.length > 0 ? '정산에서 빠짐' : '없음'}
            </p>
          </div>
        </div>

        {/* 구매수량 초과 자동 제외 안내 — 운영자가 손대는 값이 아니라 현황만 */}
        {overQuotaCount > 0 && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-700">
            구매수량 초과 발송 <b>{overQuotaCount}건</b>은 구매금액 0원 건이라 서버가 이번
            지급 집행에서 자동 제외합니다 — 도매가(발송 실비)는 낙전 정산에서 상조 상계
            예정 (위 건수·금액에 미포함).
          </div>
        )}

        {/* 제외 상세 — 운영자가 손댄 부분이라 쿠폰 단위로 재확인 + 즉시 복원 */}
        {excludedRows.length > 0 && (
          <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2.5">
            <p className="mb-1.5 text-xs font-semibold text-amber-700">
              제외된 쿠폰 (발송·환불 양쪽에서 빠집니다)
            </p>
            <ul className="max-h-32 space-y-1 overflow-y-auto text-xs">
              {excludedRows.map((r) => (
                <li key={r.key} className="flex items-center justify-between gap-2">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <span
                      className={`rounded px-1 py-0.5 text-[10px] font-medium ${
                        r.kind === 'send'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-rose-100 text-rose-700'
                      }`}
                    >
                      {r.kind === 'send' ? '발송' : '환불'}
                    </span>
                    {r.code}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className="tabular-nums text-gray-500">
                      {r.kind === 'send' ? '+' : '−'}
                      {r.amount.toLocaleString('ko-KR')}
                    </span>
                    <button
                      onClick={() => onRestore(r.couponId)}
                      className="rounded border border-amber-300 bg-white px-1.5 py-0.5 text-[10px] font-medium text-amber-700 hover:bg-amber-100"
                    >
                      복원
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* 예상 정산금 — 축 토글·제외 반영 최종값 = 서버 대조값(expectedTotalAmount) */}
        <div
          className={`mb-3 flex items-center justify-between rounded-lg px-4 py-3 ${
            effExpected < 0 ? 'bg-rose-600' : 'bg-indigo-600'
          }`}
        >
          <span className="text-sm font-medium text-white/80">
            {amountLabel(effExpected)}
          </span>
          <span className="text-2xl font-bold tabular-nums text-white">
            {formatSettlementAmount(effExpected)}
          </span>
        </div>

        {axisSplit ? (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs leading-relaxed text-amber-700">
            한쪽만 집행도 <b>조건 기반</b>으로 나갑니다 — 서버가 켠 축만 다시 조회해 위
            건수·금액과 대조 후 동결하며, 건수 제한이 없습니다. 끈 축은 미정산에 그대로
            남아 다음 집행에서 정산됩니다.
          </div>
        ) : (
          <div className="mb-4 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5 text-xs leading-relaxed text-blue-700">
            집행 시 서버가 같은 조건(상조·기간)으로 미정산을 다시 조회해 위{' '}
            <b>건수·금액과 일치하는지 대조</b>합니다. 화면 조회 이후 새 발송·환불이
            생겼으면 집행이 자동 중단되며, 그때는 목록을 새로고침해 다시 확인하면 됩니다.
            정산월은 대상 쿠폰의 최신 활동월로 자동 결정됩니다.
          </div>
        )}

        <div className="mb-5">
          <label className="mb-1 block text-sm font-medium text-gray-700">메모</label>
          <textarea
            value={memo}
            onChange={(e) => onMemo(e.target.value)}
            rows={2}
            placeholder="정산 관련 메모 (선택)"
            className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            취소
          </button>
          <button
            onClick={onConfirm}
            disabled={busy || effCount === 0}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? '집행 중...' : `집행 확인 (${effCount}건)`}
          </button>
        </div>
      </div>
    </div>
  );
};
